import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Eye, AlertCircle, Search, Upload, Sparkles, Loader2, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';


function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salio mal. Por favor, intentalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const INITIAL_FORM = {
  name: '', email: '', worker_code: '',
  dni: '', social_security: '', bank_account: '', address: '',
  work_center: '', phone: '', birth_date: '', start_date: '',
  position: '', nationality: '', emergency_contact: ''
};

export default function WorkersPage() {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await API.get('/workers');
      setWorkers(response.data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorker = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name) {
      setError('El nombre es obligatorio');
      return;
    }
    try {
      const res = await API.post('/workers', formData);
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      // Show generated credentials
      setCreatedCredentials({
        name: res.data.name,
        email: res.data.email,
        password: res.data.generated_password
      });
      fetchWorkers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeleteWorker = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/workers/${deleteId}`);
      setDeleteId(null);
      fetchWorkers();
    } catch (err) {
      console.error('Error deleting worker:', err);
    }
  };

  const handleAIExtract = useCallback(async (file) => {
    if (!file) return;
    setExtracting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/workers/extract-from-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const extracted = res.data.extracted || {};
      // Merge extracted data into form (don't overwrite email/password)
      setFormData(prev => ({
        ...prev,
        name: extracted.name || prev.name,
        dni: extracted.dni || prev.dni,
        social_security: extracted.social_security || prev.social_security,
        bank_account: extracted.bank_account || prev.bank_account,
        address: extracted.address || prev.address,
        phone: extracted.phone || prev.phone,
        birth_date: extracted.birth_date || prev.birth_date,
        nationality: extracted.nationality || prev.nationality,
        position: extracted.position || prev.position,
        work_center: extracted.work_center || prev.work_center,
      }));
    } catch (err) {
      setError('Error al extraer datos del documento. Intentalo con otra imagen.');
    } finally {
      setExtracting(false);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleAIExtract(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleAIExtract(file);
    e.target.value = '';
  };

  const filteredWorkers = workers.filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (w.worker_code || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Trabajadores</h1>
          <p className="text-sm text-gray-500 mt-0.5">{workers.length} trabajadores registrados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-orange-200 text-[#FF6B35] hover:bg-orange-50" onClick={() => navigate('/company/workers/batch')} data-testid="batch-import-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Importar con IA
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white rounded-lg shadow-sm" data-testid="create-worker-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Trabajador
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Trabajador</DialogTitle>
              <DialogDescription>Introduce los datos manualmente o sube un documento para autocompletar con IA.</DialogDescription>
            </DialogHeader>

            {/* AI Upload Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-4 text-center transition-all ${
                dragOver ? 'border-[#FF6B35] bg-orange-50' : 'border-gray-200 hover:border-orange-300'
              } ${extracting ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="ai-upload"
                data-testid="ai-upload-input"
              />
              <label htmlFor="ai-upload" className="cursor-pointer block">
                {extracting ? (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <Loader2 className="w-5 h-5 text-[#FF6B35] animate-spin" />
                    <span className="text-sm font-medium text-[#FF6B35]">Extrayendo datos con IA...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Sparkles className="w-5 h-5 text-[#FF6B35]" />
                      <span className="text-sm font-semibold text-gray-700">Autocompletar con IA</span>
                    </div>
                    <p className="text-xs text-gray-500">Arrastra o sube una foto de DNI, nomina o contrato</p>
                  </>
                )}
              </label>
            </div>

            <form onSubmit={handleCreateWorker} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Required fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Nombre completo *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Juan Garcia Lopez" required data-testid="worker-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Email de acceso</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="trabajador@correo.com (se genera si vacio)" data-testid="worker-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Codigo Trabajador</Label>
                  <Input value={formData.worker_code} onChange={(e) => setFormData({ ...formData, worker_code: e.target.value })} placeholder="EMP-001" data-testid="worker-code-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Telefono</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+34 600 000 000" />
                </div>
              </div>

              {/* Extra profile fields */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Datos personales</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">DNI / NIE</Label>
                    <Input value={formData.dni} onChange={(e) => setFormData({ ...formData, dni: e.target.value })} placeholder="12345678A" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">N. Seguridad Social</Label>
                    <Input value={formData.social_security} onChange={(e) => setFormData({ ...formData, social_security: e.target.value })} placeholder="28/12345678/90" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Cuenta Bancaria (IBAN)</Label>
                    <Input value={formData.bank_account} onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })} placeholder="ES12 3456 7890 1234 5678 90" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Telefono</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+34 600 000 000" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Fecha Nacimiento</Label>
                    <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Nacionalidad</Label>
                    <Input value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} placeholder="Española" />
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs text-gray-600">Direccion / Residencia</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Calle, numero, CP, ciudad" />
                  </div>
                </div>
              </div>

              {/* Work fields */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Datos laborales</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Puesto</Label>
                    <Input value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} placeholder="Vigilante de seguridad" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Centro de Trabajo</Label>
                    <Input value={formData.work_center} onChange={(e) => setFormData({ ...formData, work_center: e.target.value })} placeholder="Oficina central" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Fecha inicio (antiguedad)</Label>
                    <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Contacto emergencia</Label>
                    <Input value={formData.emergency_contact} onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })} placeholder="Nombre - Telefono" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setFormData(INITIAL_FORM); setError(''); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white" data-testid="submit-worker-btn">
                  Crear Trabajador
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar por nombre, email o codigo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-gray-200"
          data-testid="search-workers-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-100 bg-gray-50/50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Codigo</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Nombre</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Puesto</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Docs</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredWorkers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-400 text-sm">
                  {searchTerm ? 'No se encontraron trabajadores' : 'No hay trabajadores registrados'}
                </TableCell>
              </TableRow>
            ) : (
              filteredWorkers.map((worker) => (
                <TableRow key={worker.id} className="hover:bg-gray-50/50" data-testid={`worker-row-${worker.id}`}>
                  <TableCell>
                    {worker.worker_code ? (
                      <Badge className="bg-orange-50 text-[#FF6B35] text-xs">#{worker.worker_code}</Badge>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-gray-900">{worker.name}</TableCell>
                  <TableCell className="text-gray-600 text-sm">{worker.email}</TableCell>
                  <TableCell className="text-gray-600 text-sm">{worker.position || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="text-right font-mono text-gray-900 text-sm">{worker.document_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#FF6B35] hover:bg-orange-50" onClick={() => navigate(`/company/workers/${worker.id}`)} data-testid={`view-worker-${worker.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteId(worker.id)} data-testid={`delete-worker-${worker.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar trabajador?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara al trabajador y sus documentos asociados. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteWorker} data-testid="confirm-delete-worker">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credentials Dialog */}
      <Dialog open={!!createdCredentials} onOpenChange={() => setCreatedCredentials(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-700">Trabajador creado correctamente</DialogTitle>
            <DialogDescription>Estas son las credenciales de acceso generadas automaticamente. Guardalas o compartelas con el trabajador.</DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Nombre</span>
                  <span className="text-sm font-medium text-gray-900">{createdCredentials.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Email</span>
                  <span className="text-sm font-mono text-gray-900">{createdCredentials.email}</span>
                </div>
                {createdCredentials.password && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Contraseña</span>
                    <span className="text-sm font-mono text-gray-900">{createdCredentials.password}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center">El trabajador puede usar estas credenciales para acceder al portal</p>
              <Button className="w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white" onClick={() => setCreatedCredentials(null)}>
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
